import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';

export const ETL_RUNS_PAGE_SIZE = 5;

export interface EtlRunsPaginationProps {
  page: number;
  totalPages: number;
  totalObjects: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

export function EtlRunsPagination({
  page,
  totalPages,
  totalObjects,
  pageSize = ETL_RUNS_PAGE_SIZE,
  onPageChange,
  disabled = false,
}: EtlRunsPaginationProps) {
  if (totalObjects <= pageSize) {
    return null;
  }

  const safeTotalPages = Math.max(1, totalPages);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalObjects);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#E5E7EB] pt-4">
      <p className={cn(portalDs.bodySm, 'text-[#6B7280]')}>
        Showing
        {' '}
        <span className="font-medium text-[#111827]">
          {start}
          –
          {end}
        </span>
        {' '}
        of
        {' '}
        <span className="font-medium text-[#111827]">
          {totalObjects}
        </span>
      </p>
      <div className="flex items-center gap-2">
        <PortalDsButton
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="size-3.5" />
          Previous
        </PortalDsButton>
        <span className={cn(portalDs.bodySm, 'tabular-nums text-[#374151]')}>
          Page
          {' '}
          {page}
          {' '}
          /
          {' '}
          {safeTotalPages}
        </span>
        <PortalDsButton
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || page >= safeTotalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          Next
          <ChevronRight className="size-3.5" />
        </PortalDsButton>
      </div>
    </div>
  );
}
